import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import {
  atPacketAPI,
  clientAPI,
  staffAPI,
  providerAPI,
  lookupAPI,
} from '../services/api';
import { hasPermission } from '../utils/permissions';
import { validateAtPacketRequired } from '../utils/atPacketValidation';
import InfoIconButton from '../components/InfoIconButton';
import { mergeDsccStatusLookupForSelect } from '../utils/atPacketDsccStatusOptions';
import { formatDisplayDate } from '../utils/dateFormat';
import { ageInMonthsDisplay } from '../utils/ageInMonths';
import AtPacketProviderOfficeSelect from '../components/AtPacketProviderOfficeSelect';
import EyeIcon from '../components/icons/EyeIcon';
import { formatAtVendorSiteOptionLabel } from '../utils/providerDisplayLabel';
import { summarizeAtPacketNotificationEvents } from '../utils/atPacketNotificationSummary';
import { findExternalEmailPreviewFromEvents } from '../utils/atPacketExternalEmailPreview';
import AtPacketExternalEmailPreviewModal from '../components/AtPacketExternalEmailPreviewModal';
import './AtPackets.css';

/** UUID or legacy numeric site id — never parseInt (breaks UUIDs). */
function normalizeAtPacketSiteId(v) {
  if (v == null || v === '') return null;
  const s = String(v).trim();
  if (s === '' || s.startsWith('provider:')) return null;
  return s;
}

/**
 * Create a new AT packet with full packet fields.
 * Request name is a label for the request (for search/finding), not the client name.
 * URL param clientId or clientIdOverride (e.g. when opened from Client → AT Packets → + New AT packet) pre-fills the client.
 * When returnToClientId is set, success/cancel/back navigate to that client's AT packets list (stay in client context).
 */
function AtPacketCreate({ clientIdOverride = null, returnToClientId = null }) {
  const { tenantPath } = useTenantFromUrl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get('clientId');
  const effectiveClientId = (clientIdOverride != null && clientIdOverride !== '') ? String(clientIdOverride) : (preselectedClientId || '');

  useEffect(() => {
    if (!hasPermission('at_packets.create')) {
      const target = returnToClientId ? tenantPath(`/client/${returnToClientId}/at-packets`) : tenantPath('/lists/at-packets');
      navigate(target, { replace: true });
    }
  }, [navigate, tenantPath, returnToClientId]);

  const [clients, setClients] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [dsccStatuses, setDsccStatuses] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [clientOpenReferralId, setClientOpenReferralId] = useState(null);
  const [pageInfoOpen, setPageInfoOpen] = useState(false);
  const [requestNameInfoOpen, setRequestNameInfoOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [postCreateEmailPreview, setPostCreateEmailPreview] = useState(null);
  const [form, setForm] = useState({
    client_id: '',
    request_name: '',
    primary_language: '',
    status_id: '',
    sc_staff_id: '',
    sc_display_name: '',
    at_doctor_provider_id: '',
    at_doctor_provider_site_id: '',
    at_dr_license: '',
    requesting_provider_id: '',
    requesting_provider_site_id: '',
    ongoing_provider_id: '',
    ongoing_provider_site_id: '',
    at_vendor_provider_id: '',
    at_vendor_provider_site_id: '',
    insurance_type_id: '',
    dscc_status_id: '',
    start_date: '',
    submitted_to_atc_date: '',
    approval_date: '',
    denied_date: '',
    latest_submit_dhs_date: '',
    comment_log: '',
  });

  useEffect(() => {
    if (effectiveClientId) {
      setForm((prev) => ({ ...prev, client_id: effectiveClientId }));
    }
  }, [effectiveClientId]);

  // Derive primary language and SC from selected client
  useEffect(() => {
    if (!form.client_id || clients.length === 0) {
      if (!form.client_id) setForm((prev) => ({ ...prev, primary_language: '', sc_staff_id: '', sc_display_name: '' }));
      return;
    }
    const client = clients.find((c) => String(c.id) === String(form.client_id));
    const lang =
      client?.primary_language_id != null && languages.length > 0
        ? languages.find((l) => String(l.id) === String(client.primary_language_id))
        : null;
    const scName = client?.assigned_staff_first_name != null || client?.assigned_staff_last_name != null
      ? [client.assigned_staff_last_name, client.assigned_staff_first_name].filter(Boolean).join(', ')
      : '';
    setForm((prev) => ({
      ...prev,
      primary_language: lang ? (lang.description || lang.code || '') : '',
      sc_staff_id: client?.assigned_staff_id ?? '',
      sc_display_name: scName,
      // DSCC is inherited (read-only) from the client.
      dscc_status_id: client?.dscc_status_id != null ? String(client.dscc_status_id) : '',
    }));
  }, [form.client_id, clients, languages]);

  // Fetch open referral ID for the selected client (used for insurance eye icon link)
  useEffect(() => {
    if (!form.client_id) {
      setClientOpenReferralId(null);
      return;
    }
    let cancelled = false;
    clientAPI.getReferralInsuranceSummary(form.client_id)
      .then((res) => {
        if (!cancelled) setClientOpenReferralId(res.data?.data?.referral_id ?? null);
      })
      .catch(() => {
        if (!cancelled) setClientOpenReferralId(null);
      });
    return () => { cancelled = true; };
  }, [form.client_id]);

  // Derive AT doctor license from selected AT doctor (fetch provider when id changes)
  useEffect(() => {
    if (!form.at_doctor_provider_id) {
      setForm((prev) => (prev.at_dr_license === '' ? prev : { ...prev, at_dr_license: '' }));
      return;
    }
    let cancelled = false;
    providerAPI.getById(form.at_doctor_provider_id)
      .then((res) => {
        if (cancelled) return;
        const doc = res.data?.data;
        setForm((prev) => ({ ...prev, at_dr_license: doc?.license ?? '' }));
      })
      .catch(() => { if (!cancelled) setForm((prev) => ({ ...prev, at_dr_license: '' })); });
    return () => { cancelled = true; };
  }, [form.at_doctor_provider_id]);

  useEffect(() => {
    let cancelled = false;
    const preId = (effectiveClientId || '').trim();

    Promise.all([
      clientAPI.getAll({ page: 1, pageSize: 500 }),
      lookupAPI.getLanguages(),
      atPacketAPI.getStatuses(),
      staffAPI.getAll({ active: 'true' }),
      atPacketAPI.getDsccStatuses().catch(() => ({ data: { data: [] } })),
    ])
      .then(async ([cRes, lRes, sRes, stRes, dRes]) => {
        if (cancelled) return;
        let list = cRes.data?.data ?? [];
        // Pre-selected client (from client record URL) is often not in the first page of getAll — without this row,
        // the <select> has no matching <option> and appears blank; language/SC never derive.
        if (preId && !list.some((c) => String(c.id) === String(preId))) {
          try {
            const one = await clientAPI.getById(preId);
            const c = one.data?.data;
            if (c && !cancelled) list = [c, ...list];
          } catch (_) {
            /* keep list as-is */
          }
        }
        if (cancelled) return;
        setClients(list);
        setLanguages(lRes.data?.data ?? []);
        setStatuses(sRes.data?.data ?? []);
        setStaff(stRes.data?.data ?? []);
        setDsccStatuses(dRes.data?.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load options');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [effectiveClientId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const dsccStatusSelectOptions = useMemo(
    () => mergeDsccStatusLookupForSelect(dsccStatuses, form.dscc_status_id),
    [dsccStatuses, form.dscc_status_id]
  );

  const patchProviderWithSite = (providerField, siteField, { providerId, providerSiteId }) => {
    setForm((prev) => ({
      ...prev,
      [providerField]: providerId,
      [siteField]: providerSiteId,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const cid = form.client_id === '' ? null : (typeof form.client_id === 'string' && /^\d+$/.test(form.client_id) ? parseInt(form.client_id, 10) : form.client_id);
    const toValidate = {
      client_id: cid,
      request_name: (form.request_name || '').trim(),
      at_doctor_provider_id: form.at_doctor_provider_id || null,
      status_id: form.status_id || null,
      start_date: form.start_date || null,
      insurance_type_id: form.insurance_type_id || null,
    };
    const { valid, errors } = validateAtPacketRequired(toValidate, true);
    if (!valid) {
      setError(`Missing required fields: ${errors.join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: cid,
        request_name: (form.request_name || '').trim(),
        primary_language: (form.primary_language || '').trim() || null,
        status_id: form.status_id || null,
        sc_staff_id: form.sc_staff_id || null,
        at_doctor_provider_id: form.at_doctor_provider_id || null,
        at_doctor_provider_site_id: normalizeAtPacketSiteId(form.at_doctor_provider_site_id),
        at_dr_license: (form.at_dr_license || '').trim() || null,
        requesting_provider_id: form.requesting_provider_id || null,
        requesting_provider_site_id: normalizeAtPacketSiteId(form.requesting_provider_site_id),
        ongoing_provider_id: form.ongoing_provider_id || null,
        ongoing_provider_site_id: normalizeAtPacketSiteId(form.ongoing_provider_site_id),
        at_vendor_provider_id: form.at_vendor_provider_id || null,
        at_vendor_provider_site_id: normalizeAtPacketSiteId(form.at_vendor_provider_site_id),
        insurance_type_id: form.insurance_type_id || null,
        dscc_status_id: form.dscc_status_id ? parseInt(form.dscc_status_id, 10) : null,
        start_date: form.start_date || null,
        submitted_to_atc_date: form.submitted_to_atc_date || null,
        approval_date: form.approval_date || null,
        denied_date: form.denied_date || null,
        latest_submit_dhs_date: form.latest_submit_dhs_date || null,
        comment_log: (form.comment_log || '').trim() || null,
      };
      const res = await atPacketAPI.create(payload);
      const id = res.data?.data?.id;
      const events = res.data?.notificationEvents || [];
      const note = summarizeAtPacketNotificationEvents(events);
      const navState = note ? { atPacketNotificationSummary: note } : undefined;
      const previewPayload = findExternalEmailPreviewFromEvents(events);
      if (previewPayload && id != null) {
        const navPath = returnToClientId
          ? tenantPath(`/client/${returnToClientId}/at-packets`)
          : tenantPath(`/lists/at-packets/${id}`);
        setPostCreateEmailPreview({
          packetId: id,
          ...previewPayload,
          navPath,
          navExtraState: navState || {},
        });
        return;
      }
      if (returnToClientId) {
        navigate(tenantPath(`/client/${returnToClientId}/at-packets`), { replace: true, state: navState });
      } else if (id != null) {
        navigate(tenantPath(`/lists/at-packets/${id}`), { replace: true, state: navState });
      } else {
        navigate(tenantPath('/lists/at-packets'), { replace: true, state: navState });
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create AT packet');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="at-packets-page">
        <p>Loading…</p>
      </div>
    );
  }

  if (!hasPermission('at_packets.create')) {
    return null;
  }

  const selectedClientForDob = form.client_id
    ? clients.find((c) => String(c.id) === String(form.client_id))
    : null;
  const dobRaw = selectedClientForDob?.date_of_birth;
  const clientDobDisplay =
    dobRaw != null && String(dobRaw).trim() !== '' ? formatDisplayDate(dobRaw) : '';
  const clientDobAge = dobRaw != null ? ageInMonthsDisplay(dobRaw) : null;
  // Gate: an AT request can't be created until the client's DSCC status is set (it's inherited).
  const dsccMissing = !!selectedClientForDob && !selectedClientForDob.dscc_status_id;

  return (
    <div className="at-packets-page">
      <div className="at-packets-header">
        <div className="at-packets-title-row">
          <h1>New AT Packet</h1>
          <InfoIconButton
            ariaExpanded={pageInfoOpen}
            ariaControls="at-packet-create-page-info"
            onClick={() => setPageInfoOpen((v) => !v)}
          />
        </div>
        {pageInfoOpen && (
          <p id="at-packet-create-page-info" className="at-packets-subtitle">
            Create an Assistive Technology request packet. Request name is a label for finding this request (not the client name).
          </p>
        )}
      </div>
      {error && <p className="at-packets-error">{error}</p>}
      {dsccMissing && (
        <p className="at-packets-error">Set the client&apos;s DSCC status before creating an AT request. (Edit the client record to set DSCC.)</p>
      )}
      <form onSubmit={handleSubmit} className="at-packet-detail-form at-packet-create-form">
        <div className="form-actions form-actions--top">
          <Link to={returnToClientId ? tenantPath(`/client/${returnToClientId}/at-packets`) : tenantPath('/lists/at-packets')} className="btn-cancel">
            Cancel
          </Link>
          <button type="submit" className="btn btn-primary" disabled={saving || dsccMissing}>
            {saving ? 'Creating…' : 'Create AT packet'}
          </button>
        </div>
        <div className="at-packet-top-row">
          <div className="form-group">
            <label htmlFor="create-request_name" className="label-with-info">
              Request name *
              <InfoIconButton
                ariaExpanded={requestNameInfoOpen}
                ariaControls="create-request-name-hint"
                onClick={() => setRequestNameInfoOpen((v) => !v)}
              />
            </label>
            {requestNameInfoOpen && (
              <p id="create-request-name-hint" className="form-hint form-group-hint-below-label">
                Label for finding this request (not the client's name)
              </p>
            )}
            <input
              id="create-request_name"
              type="text"
              value={form.request_name ?? ''}
              onChange={(e) => handleChange('request_name', e.target.value)}
              placeholder="e.g. Seating 2024, Walker request – March"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="create-client_id">Client *</label>
            <select
              id="create-client_id"
              value={form.client_id ?? ''}
              onChange={(e) => handleChange('client_id', e.target.value)}
              required
            >
              <option value="">Select a client...</option>
              {clients.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {[c.last_name, c.first_name].filter(Boolean).join(', ') || `Client #${c.id}`}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <div className="form-group-dob-label-row">
              <label htmlFor="create-client_dob">Date of birth</label>
              {clientDobAge != null && (
                <span className="form-group-dob-age" aria-live="polite">
                  Age: {clientDobAge}
                </span>
              )}
            </div>
            <input
              id="create-client_dob"
              type="text"
              className="form-control-readonly"
              value={clientDobDisplay}
              readOnly
              tabIndex={-1}
              aria-readonly="true"
              placeholder="From client record"
            />
          </div>
        </div>
        <div className="at-packet-form-grid">
          <div className="form-group">
            <label htmlFor="create-primary_language">Primary language</label>
            <input
              id="create-primary_language"
              type="text"
              className="form-control-readonly"
              value={form.primary_language ?? ''}
              readOnly
              placeholder="From client record"
            />
          </div>
          <div className="form-group">
            <label htmlFor="create-status_id">Status *</label>
            <select
              id="create-status_id"
              value={form.status_id ?? ''}
              onChange={(e) => handleChange('status_id', e.target.value)}
            >
              <option value="">—</option>
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>{s.display_name || s.code}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="create-sc_staff_id">Service coordinator (SC)</label>
            <input
              id="create-sc_staff_id"
              type="text"
              className="form-control-readonly"
              value={form.sc_display_name ?? ''}
              readOnly
              placeholder="From client's assigned SC"
            />
          </div>
          <div className="form-group">
            <div className="form-label-row">
              <label htmlFor="create-at_doctor_provider_site_id">AT doctor *</label>
              {form.at_doctor_provider_id && (
                <Link
                  to={tenantPath(`/provider/${form.at_doctor_provider_id}`)}
                  className="form-field-view-icon-link"
                  title="View AT doctor"
                  aria-label="View AT doctor"
                >
                  <EyeIcon size={18} title="" />
                </Link>
              )}
            </div>
            <AtPacketProviderOfficeSelect
              id="create-at_doctor_provider_site_id"
              typeCode="DR"
              providerId={form.at_doctor_provider_id ?? ''}
              providerSiteId={form.at_doctor_provider_site_id ?? ''}
              onChange={(v) => patchProviderWithSite('at_doctor_provider_id', 'at_doctor_provider_site_id', v)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="create-at_dr_license">AT doctor license</label>
            <input
              id="create-at_dr_license"
              type="text"
              className="form-control-readonly"
              value={form.at_dr_license ?? ''}
              readOnly
              placeholder="From selected AT doctor"
            />
          </div>
          <div className="form-group">
            <div className="form-label-row">
              <label htmlFor="create-requesting_provider_site_id">Requesting provider</label>
              {form.requesting_provider_id && (
                <Link
                  to={tenantPath(`/provider/${form.requesting_provider_id}`)}
                  className="form-field-view-icon-link"
                  title="View requesting provider"
                  aria-label="View requesting provider"
                >
                  <EyeIcon size={18} title="" />
                </Link>
              )}
            </div>
            <AtPacketProviderOfficeSelect
              id="create-requesting_provider_site_id"
              providerId={form.requesting_provider_id ?? ''}
              providerSiteId={form.requesting_provider_site_id ?? ''}
              onChange={(v) => patchProviderWithSite('requesting_provider_id', 'requesting_provider_site_id', v)}
            />
          </div>
          <div className="form-group">
            <div className="form-label-row">
              <label htmlFor="create-ongoing_provider_site_id">Ongoing provider</label>
              {form.ongoing_provider_id && (
                <Link
                  to={tenantPath(`/provider/${form.ongoing_provider_id}`)}
                  className="form-field-view-icon-link"
                  title="View ongoing provider"
                  aria-label="View ongoing provider"
                >
                  <EyeIcon size={18} title="" />
                </Link>
              )}
            </div>
            <AtPacketProviderOfficeSelect
              id="create-ongoing_provider_site_id"
              providerId={form.ongoing_provider_id ?? ''}
              providerSiteId={form.ongoing_provider_site_id ?? ''}
              onChange={(v) => patchProviderWithSite('ongoing_provider_id', 'ongoing_provider_site_id', v)}
            />
          </div>
          <div className="form-group">
            <div className="form-label-row">
              <label htmlFor="create-at_vendor_provider_site_id">AT vendor</label>
              {form.at_vendor_provider_id && (
                <Link
                  to={tenantPath(`/provider/${form.at_vendor_provider_id}`)}
                  className="form-field-view-icon-link"
                  title="View AT vendor"
                  aria-label="View AT vendor"
                >
                  <EyeIcon size={18} title="" />
                </Link>
              )}
            </div>
            <AtPacketProviderOfficeSelect
              id="create-at_vendor_provider_site_id"
              typeCode="AT"
              formatOptionLabel={formatAtVendorSiteOptionLabel}
              providerId={form.at_vendor_provider_id ?? ''}
              providerSiteId={form.at_vendor_provider_site_id ?? ''}
              onChange={(v) => patchProviderWithSite('at_vendor_provider_id', 'at_vendor_provider_site_id', v)}
              searchPlaceholder="Payee name, type AT, or street…"
              resultHint="Search by payee name or type AT — up to 25 vendor offices shown."
            />
          </div>
          <div className="form-group">
            <div className="form-label-row">
              <label>Insurance</label>
              {clientOpenReferralId && form.client_id && (
                <Link
                  to={tenantPath(`/client/${form.client_id}/referral/${clientOpenReferralId}/insurance`)}
                  className="form-field-view-icon-link"
                  title="Edit insurance (referral)"
                  aria-label="Edit insurance (referral)"
                >
                  <EyeIcon size={18} title="" />
                </Link>
              )}
            </div>
            <input
              type="text"
              className="form-control-readonly"
              value="Managed on the referral record"
              readOnly
              tabIndex={-1}
              aria-readonly="true"
            />
          </div>
          <div className="form-group">
            <label htmlFor="create-dscc_status_id">DSCC status</label>
            <select
              id="create-dscc_status_id"
              value={form.dscc_status_id ?? ''}
              disabled
              title="DSCC status is inherited from the client (set it on the client record)."
            >
              <option value="">—</option>
              {dsccStatusSelectOptions.map((row) => (
                <option key={row.id} value={row.id}>{row.display_name}</option>
              ))}
            </select>
            <small style={{ color: '#6c757d' }}>Inherited from the client</small>
          </div>
          <div className="form-group">
            <label htmlFor="create-start_date">Start date *</label>
            <input
              id="create-start_date"
              type="date"
              value={form.start_date ?? ''}
              onChange={(e) => handleChange('start_date', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="create-submitted_to_atc_date">Submitted to ATC date</label>
            <input
              id="create-submitted_to_atc_date"
              type="date"
              value={form.submitted_to_atc_date ?? ''}
              onChange={(e) => handleChange('submitted_to_atc_date', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="create-approval_date">Approval date</label>
            <input
              id="create-approval_date"
              type="date"
              value={form.approval_date ?? ''}
              onChange={(e) => handleChange('approval_date', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="create-denied_date">Denied date</label>
            <input
              id="create-denied_date"
              type="date"
              value={form.denied_date ?? ''}
              onChange={(e) => handleChange('denied_date', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="create-latest_submit_dhs_date">Latest submit DHS date</label>
            <input
              id="create-latest_submit_dhs_date"
              type="date"
              value={form.latest_submit_dhs_date ?? ''}
              onChange={(e) => handleChange('latest_submit_dhs_date', e.target.value)}
            />
          </div>
          <div className="form-group form-group-full">
            <label htmlFor="create-comment_log">Comment log</label>
            <textarea
              id="create-comment_log"
              rows={4}
              value={form.comment_log ?? ''}
              onChange={(e) => handleChange('comment_log', e.target.value)}
            />
          </div>
        </div>
        <div className="form-actions">
          <Link to={returnToClientId ? tenantPath(`/client/${returnToClientId}/at-packets`) : tenantPath('/lists/at-packets')} className="btn-cancel">
            Cancel
          </Link>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Creating…' : 'Create AT packet'}
          </button>
        </div>
      </form>
      <p className="at-packets-back">
        <Link to={returnToClientId ? tenantPath(`/client/${returnToClientId}/at-packets`) : tenantPath('/lists/at-packets')}>← Back to AT Packets</Link>
      </p>
      {postCreateEmailPreview && (
        <AtPacketExternalEmailPreviewModal
          open
          packetId={postCreateEmailPreview.packetId}
          subject={postCreateEmailPreview.subject}
          body={postCreateEmailPreview.body}
          recipientsPreview={postCreateEmailPreview.recipientsPreview}
          statusCode={postCreateEmailPreview.statusCode}
          onDismiss={() => {
            navigate(postCreateEmailPreview.navPath, {
              replace: true,
              state: postCreateEmailPreview.navExtraState,
            });
            setPostCreateEmailPreview(null);
          }}
          onSendComplete={() => {
            const line =
              'If your email program opened, send the message from your mailbox so it stays on your secure account. You can edit the message before sending.';
            const prev = postCreateEmailPreview.navExtraState?.atPacketNotificationSummary;
            const combined = [prev, line].filter(Boolean).join('\n');
            navigate(postCreateEmailPreview.navPath, {
              replace: true,
              state: { ...postCreateEmailPreview.navExtraState, atPacketNotificationSummary: combined },
            });
            setPostCreateEmailPreview(null);
          }}
        />
      )}
    </div>
  );
}

export default AtPacketCreate;
